import get from 'lodash/get';
import { addDays } from 'date-fns/addDays';
import { isAfter } from 'date-fns/isAfter';
import { Offer } from '@spilca/order_ts/v1/order/model_offer_pb';
import {
  formatDate,
  fromUnixTime,
  getUnixTimeVerification
} from '@monorepo/helpers';
import { TSaveOffer } from '../../types';

const getDateAfterToday = (date: string | null, defaultDate?: string) => {
  const today = new Date();
  const currentDate = formatDate(today);
  const formDate = new Date(getUnixTimeVerification(date));
  if (date) {
    if (isAfter(formDate, today)) {
      return fromUnixTime(date);
    }
    return currentDate;
  }
  return defaultDate || currentDate;
};

const formStatusValue = (status: number) => {
  const formStatus =
    status === Offer.Status.DRAFT ? Offer.Status.DRAFT : Offer.Status.ACTIVE;

  return formStatus.toString();
};

const formatNumeric = (value: number | string, defaultValue: string = '') =>
  value ? (Number(value) / 100).toString() : defaultValue;

const offerToFormValues = (offer: Offer.AsObject): TSaveOffer => {
  return {
    status: formStatusValue(get(offer, 'status', Offer.Status.DRAFT)),
    product: get(offer, 'product', {}),
    alternativeProduct: null,
    brandName: get(offer, 'product.brandName', ''),
    quantity: formatNumeric(get(offer, 'product.quantity', 0)),
    quantityTypeName: get(offer, 'product.quantityTypeName', '').trim(),
    unit: {
      id: get(offer, 'product.quantityTypeId', null),
      fullName: get(offer, 'product.quantityTypeName', '')
    },
    price: formatNumeric(get(offer, 'price')),
    discountValue: formatNumeric(get(offer, 'discount.value', 0), ''),
    totalAmount: formatNumeric(get(offer, 'totalAmount')),
    deliveryCondition: get(offer, 'condition.deliveryCondition', ''),
    deliveryDate: getDateAfterToday(
      get(offer, 'condition.deliveryDate.seconds', '')
    ),
    deliveryPlace: get(offer, 'condition.deliveryPlace', ''),
    deliveryReceiver: get(offer, 'condition.deliveryReceiver', ''),
    paymentCondition: get(offer, 'condition.paymentCondition', ''),
    paymentDate: getDateAfterToday(
      get(offer, 'condition.paymentDate.seconds', '')
    ),
    offerCondition: get(offer, 'additionalConditions', ''),
    expiredDate: getDateAfterToday(
      get(offer, 'condition.expiredDate.seconds', ''),
      formatDate(addDays(new Date(), 7))
    ),
    description: get(offer, 'description', ''),
    currency: get(offer, 'currency', ''),
    vatApplied: Number(get(offer, 'vatApplied', false)),
    vatRate: get(offer, 'vatCondition.vatRate', 0) / 100,
    supplierId: get(offer, 'audit.createdBy', ''),
    collateral: get(offer, 'condition.financialSupportType', ''),
    deliveryPriceApplied: Number(get(offer, 'deliveryPriceApplied', false)),
    deliveryDeadline: getDateAfterToday(get(offer, 'condition.deliveryDeadline.seconds', '')),
    packaging: get(offer, 'packaging', null),
    transportType: get(offer, 'condition.transportType', ''),
    paymentTerm: get(offer, 'condition.paymentTerm', ''),
    paymentType: get(offer, 'condition.paymentType', ''),
    packageIdsList: offer.packageIdsList ?? [],
    paymentSchedule: get(offer, 'condition.paymentScheduleList', []).length > 0
      ? get(offer, 'condition.paymentScheduleList', []).map((item: any) => ({
          date: item.date?.seconds ? fromUnixTime(item.date.seconds) : '',
        payment: item.payment || 0
        }))
      : [{ date: '', payment: 0 }]
  };
};

export default offerToFormValues;
